import { Box, Card, Chip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import SpotImage from '../../assets/images/3d-icons/spot.png';
import FranceImage from '../../assets/images/3d-icons/france.png';
import { UserDeliveryZone } from '../../common/models/user.models';
import MinimumOrderValueChip from '../../common/components/MinimumOrderValueChip';
import DeliveryTimeChip from '../../common/components/DeliveryTimeChip';
import useOpenable from '../../hooks/useOpenable';
import DeliveryZoneUpsertDialog from './DeliveryZoneUpsertDialog';

interface DeliveryZoneDisplayProps {
  deliveryZone: UserDeliveryZone;
  index: number;
  zoneListLength: number;
  deleteZone?: () => void;
  readOnly?: boolean
}

export default function DeliveryZoneDisplay({ deliveryZone, index, zoneListLength, deleteZone, readOnly }: DeliveryZoneDisplayProps): React.JSX.Element {
  const { t } = useTranslation(['settings']);
  const { open, close, isOpen } = useOpenable();
  const allAreasWording = zoneListLength > 1 ? 'settings:rest_of_france' : 'settings:all_france';

  return (
    <Card
      sx={{
        backgroundColor: 'surfaceContainerLow.main',
        borderRadius: 3,
        p: 2,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="bodyLarge">
        {t('settings:zone', { index: index + 1 })}
      </Typography>
      <Box
        sx={{
          py: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <MinimumOrderValueChip
          color="surfaceContainerHigh"
          minimumOrderValue={deliveryZone.minimum_order_value}
          minimumOrderValueType={deliveryZone.minimum_order_value_type}
          negotiable={deliveryZone.negotiable_minimum_order_value}
        />
        {deliveryZone.delivery_time && <DeliveryTimeChip deliveryTime={deliveryZone.delivery_time} color="surfaceContainerHigh" />}
        {deliveryZone.administrative_areas.map(({ area }) => (
          <Chip
            variant="tag"
            color="surfaceContainerHigh"
            key={area}
            label={area === 'ALL_AREAS' ? t(allAreasWording) : area}
            icon={area === 'ALL_AREAS' ? <img src={FranceImage} /> : <img src={SpotImage} />}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {!!deleteZone && (
          <Chip
            variant="assistive"
            onClick={() => deleteZone()}
            disabled={zoneListLength === 1}
            icon={<DeleteIcon />}
            label={t('common:delete')}
          />
        )}
        {!readOnly && (
          <>
            <Chip
              variant="assistive"
              onClick={open}
              icon={<EditIcon />}
              label={t('common:modify')}
            />
            {isOpen && <DeliveryZoneUpsertDialog open onClose={close} deliveryZoneIndex={index} />}
          </>
        )}
      </Box>
    </Card>
  );
}
